import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './pharmacy-consumable-refund-bill-item.reducer';

export const PharmacyConsumableRefundBillItem = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const pharmacyConsumableRefundBillItemList = useAppSelector(state => state.pharmacyConsumableRefundBillItem.entities);
  const loading = useAppSelector(state => state.pharmacyConsumableRefundBillItem.loading);
  const totalItems = useAppSelector(state => state.pharmacyConsumableRefundBillItem.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="pharmacy-consumable-refund-bill-item-heading" data-cy="PharmacyConsumableRefundBillItemHeading">
        <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.home.title">
          Pharmacy Consumable Refund Bill Items
        </Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/pharmacy-consumable-refund-bill-item/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.home.createLabel">
              Create new Pharmacy Consumable Refund Bill Item
            </Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.pharmacyConsumableRefundBillItem.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {pharmacyConsumableRefundBillItemList && pharmacyConsumableRefundBillItemList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('chargedMrp')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.chargedMrp">Charged Mrp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('chargedMrp')} />
                </th>
                <th className="hand" onClick={sort('refundPaid')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.refundPaid">Refund Paid</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refundPaid')} />
                </th>
                <th className="hand" onClick={sort('refundQty')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.refundQty">Refund Qty</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('refundQty')} />
                </th>
                <th className="hand" onClick={sort('soldQty')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.soldQty">Sold Qty</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('soldQty')} />
                </th>
                <th className="hand" onClick={sort('manufacDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.manufacDate">Manufac Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('manufacDate')} />
                </th>
                <th className="hand" onClick={sort('expDate')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.expDate">Exp Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('expDate')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.pharmacyStock">Pharmacy Stock</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.saleBillItem">Sale Bill Item</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.refundBill">Refund Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.saleBill">Sale Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.returnBill">Return Bill</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.returnItem">Return Item</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pharmacyConsumableRefundBillItemList.map((pharmacyConsumableRefundBillItem, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button
                      tag={Link}
                      to={`/pharmacy-consumable-refund-bill-item/${pharmacyConsumableRefundBillItem.id}`}
                      color="link"
                      size="sm"
                    >
                      {pharmacyConsumableRefundBillItem.id}
                    </Button>
                  </td>
                  <td>{pharmacyConsumableRefundBillItem.chargedMrp}</td>
                  <td>{pharmacyConsumableRefundBillItem.refundPaid}</td>
                  <td>{pharmacyConsumableRefundBillItem.refundQty}</td>
                  <td>{pharmacyConsumableRefundBillItem.soldQty}</td>
                  <td>{pharmacyConsumableRefundBillItem.manufacDate}</td>
                  <td>{pharmacyConsumableRefundBillItem.expDate}</td>
                  <td>{pharmacyConsumableRefundBillItem.active ? 'true' : 'false'}</td>
                  <td>{pharmacyConsumableRefundBillItem.cursor}</td>
                  <td>{pharmacyConsumableRefundBillItem.timestampentry}</td>
                  <td>{pharmacyConsumableRefundBillItem.microSec}</td>
                  <td>{pharmacyConsumableRefundBillItem.lastupdated}</td>
                  <td>
                    {pharmacyConsumableRefundBillItem.pharmacyStock ? (
                      <Link to={`/pharmacy-consumable-stock/${pharmacyConsumableRefundBillItem.pharmacyStock.id}`}>
                        {pharmacyConsumableRefundBillItem.pharmacyStock.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyConsumableRefundBillItem.saleBillItem ? (
                      <Link to={`/pharmacy-consumable-bill-item/${pharmacyConsumableRefundBillItem.saleBillItem.id}`}>
                        {pharmacyConsumableRefundBillItem.saleBillItem.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyConsumableRefundBillItem.refundBill ? (
                      <Link to={`/pharmacy-refund-bill/${pharmacyConsumableRefundBillItem.refundBill.id}`}>
                        {pharmacyConsumableRefundBillItem.refundBill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyConsumableRefundBillItem.saleBill ? (
                      <Link to={`/pharmacy-bill/${pharmacyConsumableRefundBillItem.saleBill.id}`}>
                        {pharmacyConsumableRefundBillItem.saleBill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyConsumableRefundBillItem.returnBill ? (
                      <Link to={`/pharmacy-return-bill/${pharmacyConsumableRefundBillItem.returnBill.id}`}>
                        {pharmacyConsumableRefundBillItem.returnBill.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {pharmacyConsumableRefundBillItem.returnItem ? (
                      <Link to={`/pharmacy-consumable-return-bill-item/${pharmacyConsumableRefundBillItem.returnItem.id}`}>
                        {pharmacyConsumableRefundBillItem.returnItem.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/pharmacy-consumable-refund-bill-item/${pharmacyConsumableRefundBillItem.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/pharmacy-consumable-refund-bill-item/${pharmacyConsumableRefundBillItem.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/pharmacy-consumable-refund-bill-item/${pharmacyConsumableRefundBillItem.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.pharmacyConsumableRefundBillItem.home.notFound">
                No Pharmacy Consumable Refund Bill Items found
              </Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={pharmacyConsumableRefundBillItemList && pharmacyConsumableRefundBillItemList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PharmacyConsumableRefundBillItem;
